import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import DataTable from "react-data-table-component";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";
import FullScreenLoader from "../../components/FullScreenLoader";

function PupilActivityEnrollmentForm() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  const userId = localStorage.getItem("userId");
  const roleId = localStorage.getItem("roleId");

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      id: 0,
      campusActivityId: null,
      studentId: null,
      campusActivitySlotId: null,
      createdBy: localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
    },
  });

  const [activityCategoryData, setActivityCategoryData] = useState([]);
  const [activityVenueData, setActivityVenueData] = useState([]);
  const [activitySlotsData, setActivitySlotsData] = useState([]);

  const [loading, setLoading] = useState(false);

  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response?.commonSettingDetail);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  useEffect(() => {
    fetchCommonSettings("activityCategoryId", setActivityCategoryData);
    fetchCommonSettings("activityVenueId", setActivityVenueData);
  }, [token]);

  const [activitiesData, setActivitiesData] = useState([]);
  const [studentsData, setStudentsData] = useState([]);

  const [activityApplicationsData, setActivityApplicationsData] = useState([]);

  const getActivitiesList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `CampusActivity/GetActivities?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActivitiesData(response);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const getActivityApplicationsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `CampusActivity/GetAllActivityInvoicesByPrentId?parentId=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActivityApplicationsData(response?.invoiceReturnResponseList);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    getActivitiesList();
    getActivityApplicationsList();
  }, [triggerRerender]);

  const fetchStudentsByActivityAndParentId = async () => {
    try {
      const response = await get(
        `CampusActivity/GetActivityStudentsByPrentSectionActivityId?activityId=${selectedActivityId}&userId=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStudentsData(response || []);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  useEffect(() => {
    if (selectedActivityId) {
      fetchStudentsByActivityAndParentId();
    }
  }, [selectedActivityId]);

  const handleActivityChange = async (activityId) => {
    try {
      const response = await get(
        `CampusActivity/GetActivityById?activityId=${activityId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const activity = response?.campusActivityDto;

      setActivitySlotsData(activity.campusActivitySlotsDtos);

      setValue("objective", activity.objective);
      setValue("activityVenueId", activity.activityVenueId);
      setValue("scheduleDateTime", activity.scheduleDateTime.split("T")[0]);
      setValue("activityFee", activity.activityFee);
      setValue("maxCapicity", activity.maxCapicity);
      setValue("description", activity.description);
      setValue("studentId", null);
      setValue("campusActivitySlotId", null);
    } catch (error) {
      console.error("Error editing section:", error);
    }
  };

  const handleDelete = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this record?"
    );

    if (confirmDelete) {
      try {
        const response = await post(
          `CampusActivity/RemoveActivityInvoice?id=${rowId}`,
          // formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.isSuccess) {
          toast.success(response.message || "Success!");
          setTriggerRerender((prev) => !prev);
        } else {
          toast.error(response.message || "An error occurred while deleting.");
        }
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
  };

  const handlePrintActivityChallanAction = async (id) => {
    if (!id) {
      alert("Error");
      return;
    }

    setLoading(true);

    const API_BASE_URL = process.env.REACT_APP_API_URL;

    try {
      const response = await fetch(
        `${API_BASE_URL}CampusActivity/GetSlotInvoiceData?id=${id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      window.open(url);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Error opening the invoice:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "60px",
    },
    {
      name: "Pupil",
      selector: (row) => row.pupileName,
      sortable: false,
      width: "150px",
    },
    {
      name: "Activity",
      selector: (row) => row.activityName,
      sortable: false,
      width: "150px",
    },

    {
      name: "Slot",
      selector: (row) => row.slotName,
      sortable: false,
      // width: "150px",
    },
    {
      name: "Venue",
      selector: (row) => row.locationName,
      sortable: false,
      // width: "150px",
    },
    {
      name: "Fee",
      selector: (row) => row.fee,
      sortable: false,
      // width: "150px",
    },
    {
      name: "Status",
      selector: (row) => (
        <span
          className={`badge fs-10 ${
            row.status === "Paid" ? "bg-success" : "bg-warning"
          }`}
        >
          {row.status}
        </span>
      ),
      sortable: false,
      // width: "150px",
    },
    {
      name: "Attendance",
      cell: (row) => (
        <span
          className={`badge fs-10 ${
            row.attendance === "P" ? "bg-success" : "bg-danger"
          }`}
        >
          {row.attendance}
        </span>
      ),
      sortable: false,
      width: "120px",
    },
    {
      name: "Rating",
      selector: (row) => row.rating,
      cell: (row) => (
        <div>
          {Array.from({ length: 5 }, (_, index) => (
            <span
              key={index}
              className={`fa fa-star ${index < row.rating ? "checked" : ""}`}
              style={{
                margin: "0 2px",
                color: index < row.rating ? "gold" : "lightgray",
              }}
            ></span>
          ))}
        </div>
      ),
      sortable: false,
      width: "150px",
    },
    {
      name: "Remarks",
      selector: (row) => <span className="fs-10">{row.remarks}</span>,
      sortable: false,
      width: "250px",
    },
    {
      name: "Action",
      width: "80px",
      selector: (row) => (
        <div className="d-flex gap-1">
          <button
            type="button"
            className="btn p-0"
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Print Challan"
            onClick={() => handlePrintActivityChallanAction(row.id)}
          >
            <i className="fas fa-print"></i>
          </button>
          <button
            type="button"
            className="btn p-0"
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Delete"
            onClick={() => handleDelete(row.id)}
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formData = {
      // ...data,
      id: 0,
      campusActivityId: data?.campusActivityId?.value,
      studentId: data?.studentId?.value,
      campusActivitySlotId: data?.campusActivitySlotId?.value,
      createdBy: localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
    };

    // console.log(formData);
    // return;

    try {
      const response = await post(
        "CampusActivity/UpdateActivityApplication",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success("Success!");
        setTriggerRerender((prev) => !prev);
        reset();
        // id: 0,
        // campusActivityId: null,
        // studentId: null,
        // campusActivitySlotId: null,
        // createdBy: localStorage.getItem("userId"),
        // updatedBy: localStorage.getItem("userId"),
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Activity Enrollment</h2>
      </div>
      <div className="row g-3">
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <form className="row g-3" onSubmit={handleSubmit(saveData)}>
                <div className="col-md-12">
                  <label className="form-label">
                    Activity
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="campusActivityId"
                    control={control}
                    rules={{
                      required: "This field is required.",
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={activitiesData.map((item) => ({
                          value: item.activityId,
                          label: item.activityName,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("campusActivityId", null);
                            setSelectedActivityId(null);
                            reset();
                          } else {
                            setValue("campusActivityId", selectedOption);
                            setSelectedActivityId(selectedOption.value);
                            handleActivityChange(selectedOption.value);
                          }
                          trigger("campusActivityId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                      />
                    )}
                  />
                  {errors.campusActivityId && (
                    <small className="text-danger">
                      {errors.campusActivityId.message}
                    </small>
                  )}
                </div>
                <div className="m-0"></div>
                <div className="col-md-6">
                  <label className="form-label">Activity Date</label>
                  <input
                    {...register(`scheduleDateTime`)}
                    type="date"
                    className="form-control"
                    disabled={true}
                  />
                  {errors.scheduleDateTime && (
                    <small className="text-danger">
                      {errors.scheduleDateTime.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Venue/Location</label>
                  <Controller
                    name="activityVenueId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        options={activityVenueData.map((item) => ({
                          value: item.id,
                          label: item.value,
                        }))}
                        isClearable
                        isDisabled
                        onChange={(selectedOption) =>
                          field.onChange(
                            selectedOption ? selectedOption.value : ""
                          )
                        }
                        value={
                          activityVenueData
                            .map((item) => ({
                              value: item.id,
                              label: item.value,
                            }))
                            .find((option) => option.value === field.value) ||
                          null
                        }
                      />
                    )}
                  />

                  {errors.activityVenueId && (
                    <small className="text-danger">
                      {errors.activityVenueId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Fee Applicable</label>
                  <input
                    {...register(`activityFee`)}
                    type="number"
                    className="form-control"
                    disabled={true}
                  />
                  {errors.activityFee && (
                    <small className="text-danger">
                      {errors.activityFee.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Objective</label>
                  <input
                    {...register(`objective`)}
                    type="text"
                    className="form-control"
                    disabled={true}
                  />
                  {errors.objective && (
                    <small className="text-danger">
                      {errors.objective.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Maximum participant capacity
                  </label>
                  <input
                    {...register(`maxCapicity`)}
                    type="number"
                    className="form-control"
                    disabled={true}
                  />
                  {errors.maxCapicity && (
                    <small className="text-danger">
                      {errors.maxCapicity.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Description</label>
                  <textarea
                    {...register("description")}
                    className="form-control"
                    disabled={true}
                  ></textarea>
                  {errors.description && (
                    <small className="text-danger">
                      {errors.description.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Pupil<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="studentId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={studentsData.map((student) => ({
                          value: student.id,
                          label:
                            student.studentName +
                            " — " +
                            student.code +
                            " — " +
                            student.className +
                            "(" +
                            student.sectionName +
                            ")",
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setSelectedStudentId(null);
                            setValue("studentId", null);
                          } else {
                            setSelectedStudentId(
                              selectedOption ? selectedOption.value : null
                            );
                            setValue("studentId", selectedOption);
                          }
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.studentId && (
                    <small className="text-danger">
                      {errors.studentId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Slot<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="campusActivitySlotId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={activitySlotsData.map((slot) => ({
                          value: slot.id,
                          label:
                            slot.name +
                            " (" +
                            slot.slotStartTime +
                            "-" +
                            slot.slotEndTime +
                            ")",
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("campusActivitySlotId", null);
                          } else {
                            setValue("campusActivitySlotId", selectedOption);
                          }
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.campusActivitySlotId && (
                    <small className="text-danger">
                      {errors.campusActivitySlotId.message}
                    </small>
                  )}
                </div>

                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-8">
          <div className="card">
            <div className="card-body">
              <DataTable
                columns={columns}
                data={activityApplicationsData}
                pagination
                paginationPerPage={rowsPerPage}
                paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                dense
                customStyles={customTableStyles}
                highlightOnHover={true}
                onChangePage={setCurrentPage}
                onChangeRowsPerPage={setRowsPerPage}
              />
            </div>
          </div>
        </div>
      </div>

      {loading && <FullScreenLoader />}
    </>
  );
}

export default PupilActivityEnrollmentForm;
